import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage: 'url(/assets/images/FortifiedMindSplash1-1769379659227.jpeg)'
      }}>

      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/40"></div>
      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header with Back Button */}
        <div className="p-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 transition-colors">

            <ArrowLeft className="w-5 h-5" />
            <span className="text-lg font-medium">Back to Login</span>
          </Link>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-4xl bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12 max-h-[85vh] overflow-y-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Privacy Policy
            </h1>
            
            <p className="text-center text-gray-600 mb-8">
              <strong>Effective Date:</strong> February 1, 2026<br />
              <strong>Last Updated:</strong> February 1, 2026
            </p>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              {/* Introduction */}
              <section>
                <p className="mb-4">
                  Welcome to <strong>Fortified Mind</strong> ("we," "us," or "our"). Fortified Mind is a non-profit scripture memory application available at <a href="https://fortifiedmind.org" className="text-blue-600 hover:underline">fortifiedmind.org</a>. We are committed to protecting your privacy and ensuring transparency about how we collect, use, and safeguard your personal information.
                </p>
                <p className="mb-4">
                  This Privacy Policy explains our practices regarding data collection and use. By using Fortified Mind, you agree to the terms outlined in this policy.
                </p>
              </section>

              {/* 1. Information We Collect */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
                <p className="mb-3">We collect the following types of information to provide and improve our services:</p>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">1.1 Information You Provide</h3>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Account Information:</strong> Email address and password when you create an account</li>
                  <li><strong>User-Generated Content:</strong> Scripture verses you save, notes, reflections, study materials, and other content you create within the app</li>
                  <li><strong>Profile Information:</strong> Optional profile details such as username, avatar, and preferences</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">1.2 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Usage Data:</strong> Information about how you interact with the app, including features used, time spent, and navigation patterns</li>
                  <li><strong>Analytics Data:</strong> We may use Google Analytics (planned future integration) to understand user behavior and improve our services</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers</li>
                  <li><strong>Performance Data:</strong> Error logs and diagnostic information to maintain app functionality</li>
                </ul>
              </section>

              {/* 2. How We Use Your Information */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
                <p className="mb-3">We use your information for the following purposes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Service Delivery:</strong> To provide, maintain, and improve Fortified Mind's scripture memory features</li>
                  <li><strong>Authentication:</strong> To verify your identity and secure your account</li>
                  <li><strong>Personalization:</strong> To customize your experience, including spaced repetition schedules and progress tracking</li>
                  <li><strong>Communication:</strong> To send service-related notifications, updates, and respond to your inquiries via email</li>
                  <li><strong>Analytics:</strong> To analyze usage patterns and improve app functionality and user experience</li>
                  <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security threats</li>
                  <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
                </ul>
              </section>

              {/* 3. Third-Party Services */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. Third-Party Services We Use</h2>
                <p className="mb-3">Fortified Mind integrates with the following third-party services to provide our functionality:</p>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.1 Supabase</h3>
                <p className="mb-2">
                  <strong>Purpose:</strong> Authentication, database hosting, and backend infrastructure<br />
                  <strong>Data Shared:</strong> Email, password (encrypted), user-generated content, usage data<br />
                  <strong>Privacy Policy:</strong> <a href="https://supabase.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://supabase.com/privacy</a>
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.2 Resend</h3>
                <p className="mb-2">
                  <strong>Purpose:</strong> Email delivery for account verification, notifications, and communications<br />
                  <strong>Data Shared:</strong> Email address, message content<br />
                  <strong>Privacy Policy:</strong> <a href="https://resend.com/legal/privacy-policy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://resend.com/legal/privacy-policy</a>
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.3 Stripe (Future Integration)</h3>
                <p className="mb-2">
                  <strong>Purpose:</strong> Processing donations and purchases of materials<br />
                  <strong>Data Shared:</strong> Payment information (processed securely by Stripe, not stored by us)<br />
                  <strong>Privacy Policy:</strong> <a href="https://stripe.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.4 Google Analytics (Future Integration)</h3>
                <p className="mb-2">
                  <strong>Purpose:</strong> Website analytics and user behavior analysis<br />
                  <strong>Data Shared:</strong> Usage data, device information, anonymized user interactions<br />
                  <strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>
                </p>

                <p className="mt-4 text-sm italic">
                  We do not sell, rent, or share your personal information with third parties for their marketing purposes.
                </p>
              </section>

              {/* 4. Data Security */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. Data Security</h2>
                <p className="mb-3">
                  We take data security seriously and implement industry-standard measures to protect your information:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted using SSL/TLS protocols</li>
                  <li><strong>Password Security:</strong> Passwords are hashed and encrypted using industry-standard algorithms</li>
                  <li><strong>Infrastructure Security:</strong> Our data is hosted on Supabase's secure infrastructure, which implements enterprise-grade security measures including regular security audits, firewalls, and access controls</li>
                  <li><strong>Access Controls:</strong> We limit access to personal information to authorized personnel only</li>
                  <li><strong>Regular Monitoring:</strong> We monitor our systems for potential vulnerabilities and security threats</li>
                </ul>
                <p className="text-sm italic">
                  While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your data to the best of our ability.
                </p>
              </section>

              {/* 5. Your Rights and Choices */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. Your Rights and Choices</h2>
                <p className="mb-3">You have the following rights regarding your personal information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> You can access and review your personal information through your account settings</li>
                  <li><strong>Correction:</strong> You can update or correct your information at any time</li>
                  <li><strong>Data Portability:</strong> You can request a copy of your data in a portable format</li>
                  <li><strong>Deletion:</strong> You can delete your account and associated data at any time through account settings or by contacting us</li>
                  <li><strong>Opt-Out:</strong> You can opt out of non-essential communications</li>
                  <li><strong>Withdraw Consent:</strong> You can withdraw consent for data processing where applicable</li>
                </ul>
                <p className="mt-3">
                  To exercise these rights, please <a href="/contact-us" className="text-blue-600 hover:underline">contact us</a>.
                </p>
              </section>

              {/* 6. Children's Privacy */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Children's Privacy</h2>
                <p className="mb-3">
                  Fortified Mind is designed to be accessible to users of all ages, including children. We are committed to protecting the privacy of children in compliance with the Children's Online Privacy Protection Act (COPPA) and other applicable laws.
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Parental Consent:</strong> We encourage parents and guardians to supervise their children's use of Fortified Mind</li>
                  <li><strong>Minimal Data Collection:</strong> We collect only the minimum information necessary to provide our services</li>
                  <li><strong>No Marketing to Children:</strong> We do not knowingly market to or target children under 13</li>
                  <li><strong>Parental Rights:</strong> Parents can request access to, correction of, or deletion of their child's information by contacting us</li>
                </ul>
                <p>
                  If you believe we have inadvertently collected information from a child under 13 without proper consent, please <a href="/contact-us" className="text-blue-600 hover:underline">contact us</a> immediately.
                </p>
              </section>

              {/* 7. User-Generated Content */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. User-Generated Content and Liability</h2>
                <p className="mb-3">
                  Fortified Mind contains religious content and allows users to create and share their own content, including notes, reflections, and study materials.
                </p>
                <p className="mb-3">
                  <strong>Content Disclaimer:</strong> User-generated content represents the views and opinions of individual users, not Fortified Mind. We do not endorse, verify, or assume responsibility for the accuracy, appropriateness, or legality of user-generated content.
                </p>
                <p className="mb-3">
                  <strong>User Responsibility:</strong> By using Fortified Mind, you agree that:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>You are responsible for the content you create and share</li>
                  <li>You will not post content that is illegal, harmful, offensive, or violates others' rights</li>
                  <li>You understand that religious content may reflect diverse theological perspectives</li>
                  <li>You release Fortified Mind from liability related to user-generated content</li>
                </ul>
                <p>
                  We reserve the right to remove content that violates our terms or is reported as inappropriate, but we are not obligated to monitor all user-generated content.
                </p>
              </section>

              {/* 8. International Users */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. International Users and Data Transfers</h2>
                <p className="mb-3">
                  Fortified Mind is operated from Alabama, United States, and is designed for worldwide use. If you access our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States or other countries where our service providers operate.
                </p>
                <p className="mb-3">
                  By using Fortified Mind, you consent to the transfer of your information to countries that may have different data protection laws than your country of residence.
                </p>
                <p>
                  <strong>GDPR Compliance (European Users):</strong> If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including the right to lodge a complaint with a supervisory authority.
                </p>
              </section>

              {/* 9. Cookies and Tracking */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Cookies and Tracking Technologies</h2>
                <p className="mb-3">
                  We use cookies and similar tracking technologies to enhance your experience and collect usage data:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Essential Cookies:</strong> Required for authentication and core functionality</li>
                  <li><strong>Analytics Cookies:</strong> Used to understand how users interact with our app (Google Analytics, when implemented)</li>
                  <li><strong>Preference Cookies:</strong> Store your settings and preferences</li>
                </ul>
                <p>
                  You can control cookies through your browser settings, but disabling certain cookies may affect app functionality.
                </p>
              </section>

              {/* 10. Data Retention */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Data Retention</h2>
                <p className="mb-3">
                  We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. Specifically:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Data:</strong> Retained until you delete your account</li>
                  <li><strong>User Content:</strong> Retained until you delete it or close your account</li>
                  <li><strong>Analytics Data:</strong> Retained in anonymized form for analysis purposes</li>
                  <li><strong>Legal Requirements:</strong> Some data may be retained longer if required by law</li>
                </ul>
              </section>

              {/* 11. Advertising and Promotions */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Advertising and Promotions</h2>
                <p className="mb-3">
                  Fortified Mind does not use third-party advertising services. However, we may occasionally promote books, media, or resources that we believe align with our mission on a future blog associated with the site. These promotions are editorial recommendations and do not involve sharing your personal information with third parties.
                </p>
              </section>

              {/* 12. Newsletter Communications */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. Newsletter Communications</h2>
                <p className="mb-3">
                  Fortified Mind offers an optional newsletter to keep you informed about:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>New features and updates to the app</li>
                  <li>Scripture memory tips and encouragement</li>
                  <li>Community highlights and user stories</li>
                  <li>Recommended resources and content</li>
                </ul>
                <p className="mb-3">
                  <strong>Subscription:</strong> Newsletter subscription is entirely optional. You may choose to subscribe during account creation or through your account settings.
                </p>
                <p className="mb-3">
                  <strong>Opt-Out:</strong> You have the right to opt out of newsletter communications at any time. You can unsubscribe by:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Clicking the "Unsubscribe" link at the bottom of any newsletter email</li>
                  <li>Updating your communication preferences in your account settings</li>
                  <li>Contacting us directly via our <a href="/contact-us" className="text-blue-600 hover:underline">contact page</a></li>
                </ul>
                <p className="text-sm italic">
                  Note: Opting out of the newsletter will not affect essential service-related emails, such as account verification, password resets, or important security notifications.
                </p>
              </section>

              {/* 13. Changes to This Policy */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. Changes to This Privacy Policy</h2>
                <p className="mb-3">
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make changes:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>We will update the "Last Updated" date at the top of this policy</li>
                  <li>We will notify you of material changes via email or prominent notice within the app</li>
                  <li>Your continued use of Fortified Mind after changes constitutes acceptance of the updated policy</li>
                </ul>
                <p>
                  We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
                </p>
              </section>

              {/* 14. Contact Us */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">14. Contact Us</h2>
                <p className="mb-3">
                  If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="mb-2"><strong>Fortified Mind</strong></p>
                  <p className="mb-2">Email: <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us</a></p>
                  <p className="mb-2">Website: <a href="https://fortifiedmind.org" className="text-blue-600 hover:underline">fortifiedmind.org</a></p>
                  <p>Location: Alabama, United States</p>
                </div>
                <p className="mt-4">
                  We will respond to your inquiry within a reasonable timeframe, typically within 30 days.
                </p>
              </section>

              {/* Acknowledgment */}
              <section className="border-t pt-6 mt-8">
                <p className="text-center font-semibold text-gray-900">
                  By using Fortified Mind, you acknowledge that you have read, understood, and agree to this Privacy Policy.
                </p>
              </section>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 text-center">
          <p className="text-white text-sm">
            © 2026 Fortified Mind. All rights reserved.
          </p>
        </div>
      </div>
    </div>);

}